import { useTranslation } from 'react-i18next'
import { FONT_PAIRS, familiesOfPairs, stackFor } from '../../render/fonts'
import { useGoogleFonts } from '../../hooks/useGoogleFonts'
import { Theme } from '../../render/theme'
import FontFamilyFields from '../FontFamilyFields/FontFamilyFields'
import styles from './PropertiesPanel.module.scss'

interface FontPickerProps {
  theme: Theme
  onChange: (theme: Theme) => void
}

const PAIR_IDS = FONT_PAIRS.map(pair => pair.id)
const PAIR_FAMILIES = familiesOfPairs(PAIR_IDS)

/**
 * The page's typefaces: eight ready-made pairs, and above them the whole
 * catalogue, one face at a time.
 *
 * The two fields come first, because they are the answer to "I want THAT one".
 * The pairs underneath are the short way in for somebody who does not, and they
 * stay the fallback: a family chosen for one half overrides the pair for that
 * half alone. Picking a pair again clears both, because that is what picking a
 * pair means.
 */
function FontPicker({ theme, onChange }: FontPickerProps) {
  const { t } = useTranslation()

  // Up front, only what is drawn without scrolling: the eight specimens. The
  // two chosen faces are asked for by the fields themselves.
  useGoogleFonts(PAIR_FAMILIES)

  return (
    <div className={styles.fontList}>
      <FontFamilyFields theme={theme} onChange={onChange} />

      <p className={`body-small ${styles.fontHint}`}>
        {t('WEBSITE.FONTS.PAIRS_HINT')}
      </p>

      {FONT_PAIRS.map(pair => (
        <button
          key={pair.id}
          type="button"
          className={`${styles.fontOption} ${
            pair.id === theme.fontPairId && !theme.fonts
              ? styles.fontOptionActive
              : ''
          }`}
          aria-pressed={pair.id === theme.fontPairId && !theme.fonts}
          onClick={() =>
            onChange({ ...theme, fontPairId: pair.id, fonts: undefined })
          }
        >
          <span
            className={styles.fontSampleHeading}
            style={{ fontFamily: stackFor(pair.heading) }}
          >
            {t('WEBSITE.FONTS.SAMPLE_HEADING')}
          </span>
          <span
            className={styles.fontSampleBody}
            style={{ fontFamily: stackFor(pair.body) }}
          >
            {t('WEBSITE.FONTS.SAMPLE_BODY')}
          </span>
          <span className={`label-small ${styles.fontName}`}>
            {`${t(`WEBSITE.THEME_FONTS.${pair.id}`)} · ${pair.heading} + ${pair.body}`}
          </span>
        </button>
      ))}
    </div>
  )
}

export default FontPicker
